import { subscribeToImageEdits } from './services/imageEditRefresh.js';
/**
 * Main App Component
 *
 * Router setup, auth context, realtime event listeners
 */

import React, { useEffect, Suspense, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore, watchSession } from './stores/authStore.js';
import { useProjectStore } from './stores/projectStore.js';
import { useGenerationStore } from './stores/generationStore.js';
import { useShotlistStore } from './stores/shotlistStore.js';
import { useVisionBoardStore } from './stores/visionBoardStore.js';
import { on, off, getCurrentProjectId, dropPresence } from './services/realtime.js';
import { invalidateUrl } from './services/storageUrls.js';
import { isExitingProject } from './utils/projectExit.js';
import {
  DashboardNotice,
  replaceWithWebApp,
  visionPath,
  webDashboardUrl,
  webLoginUrl,
} from './utils/webAppLinks.js';
// Keep LoadingSpinner & Sidebar eager — they're needed immediately for the app shell
import { LoadingSpinner } from './components/common/LoadingSpinner.jsx';
import { ContentLoading } from './components/common/ContentLoading.jsx';
import { Sidebar } from './components/sidebar/Sidebar.jsx';

// Lazy-load page-level components (code-split per route)
const AuthLayout = React.lazy(() => import('./components/auth/AuthLayout.jsx').then(m => ({ default: m.AuthLayout })));
const ProjectLayout = React.lazy(() => import('./components/project/ProjectLayout.jsx').then(m => ({ default: m.ProjectLayout })));
const ProjectSettings = React.lazy(() => import('./components/project/ProjectSettings.jsx').then(m => ({ default: m.ProjectSettings })));
const ArtboardContainer = React.lazy(() => import('./components/artboard/ArtboardContainer.jsx').then(m => ({ default: m.ArtboardContainer })));
const GenerationPage = React.lazy(() => import('./components/generation/GenerationPage.jsx').then(m => ({ default: m.GenerationPage })));
const AccountSettings = React.lazy(() => import('./components/account/AccountSettings.jsx').then(m => ({ default: m.AccountSettings })));
const StoryboardPage = React.lazy(() => import('./components/storyboard/StoryboardPage.jsx').then(m => ({ default: m.StoryboardPage })));
const ShotlistPage = React.lazy(() => import('./components/shotlist/ShotlistPage.jsx').then(m => ({ default: m.ShotlistPage })));
const VisionBoardPage = React.lazy(() => import('./components/visionboard/VisionBoardPage.jsx').then(m => ({ default: m.VisionBoardPage })));
const AcceptInvitePage = React.lazy(() => import('./components/invite/AcceptInvitePage.jsx').then(m => ({ default: m.AcceptInvitePage })));
const StorageBreakdown = React.lazy(() => import('./components/account/StorageBreakdown.jsx').then(m => ({ default: m.StorageBreakdown })));
const CreditsPage = React.lazy(() => import('./components/account/CreditsPage.jsx').then(m => ({ default: m.CreditsPage })));

// window.location.pathname includes the Vite base (e.g. /app in dev) that the router's
// basename strips; router-relative checks must strip it the same way.
const APP_BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
const appPathname = () => {
  const { pathname } = window.location;
  return APP_BASE && pathname.startsWith(APP_BASE) ? pathname.slice(APP_BASE.length) || '/' : pathname;
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg p-8">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-semibold text-text-primary mb-2">Something went wrong</h1>
            <p className="text-text-secondary mb-6">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function FullPageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function SessionCheckFailed({ onRetry }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-8">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-semibold text-text-primary mb-2">Couldn't check your session</h1>
        <p className="text-text-secondary mb-6">Check your connection and try again.</p>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isLoggedOut = useAuthStore((state) => state.isLoggedOut);
  const location = useLocation();
  const signedOut = isInitialized && !isAuthenticated;
  const here = location.pathname + location.search + location.hash;

  useEffect(() => {
    if (!signedOut) return;
    replaceWithWebApp(isLoggedOut ? webLoginUrl() : webLoginUrl(visionPath(here)));
  }, [signedOut, isLoggedOut, here]);

  return isAuthenticated ? children : <FullPageLoading />;
}

function RootRedirect() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isLoggedOut = useAuthStore((state) => state.isLoggedOut);

  useEffect(() => {
    if (isAuthenticated) replaceWithWebApp(webDashboardUrl());
    else if (isInitialized) replaceWithWebApp(isLoggedOut ? webLoginUrl() : webLoginUrl('/dashboard'));
  }, [isAuthenticated, isInitialized, isLoggedOut]);

  return <FullPageLoading />;
}

/**
 * Shared authenticated layout with persistent Sidebar.
 * Renders once for all authenticated routes so the sidebar
 * doesn't unmount/remount on navigation.
 */
const GracePeriodBanner = React.lazy(() => import('./components/common/GracePeriodBanner.jsx').then(m => ({ default: m.GracePeriodBanner })));

export function AppLayout() {
  const user = useAuthStore((state) => state.user);
  const showGraceBanner = (user?.subscription_status === 'lapsed' && user?.grace_period_start)
    || user?.subscription_status === 'past_due';

  return (
    <div className="h-dvh bg-bg flex flex-col md:flex-row">
      {/* Persistent Sidebar - Desktop */}
      <Sidebar />

      {/* Main Content - changes per route */}
      <main className="flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden">
        {showGraceBanner && (
          <Suspense fallback={null}>
            <GracePeriodBanner user={user} />
          </Suspense>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <Suspense fallback={<ContentLoading />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
}

// Realtime Event Listener
function RealtimeEventListener() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // Subscribe to store actions (these are stable functions, but using selectors for consistency)
  const addImage = useProjectStore((state) => state.addImage);
  const updateImage = useProjectStore((state) => state.updateImage);
  const removeImage = useProjectStore((state) => state.removeImage);
  const moveImage = useProjectStore((state) => state.moveImage);
  const reorderImages = useProjectStore((state) => state.reorderImages);
  const addCategory = useProjectStore((state) => state.addCategory);
  const updateCategory = useProjectStore((state) => state.updateCategory);
  const removeCategory = useProjectStore((state) => state.removeCategory);
  const addMember = useProjectStore((state) => state.addMember);
  const updateMemberRole = useProjectStore((state) => state.updateMemberRole);
  const removeMember = useProjectStore((state) => state.removeMember);
  const addPendingInvite = useProjectStore((state) => state.addPendingInvite);
  const removePendingInvite = useProjectStore((state) => state.removePendingInvite);

  const addPendingGeneration = useGenerationStore((state) => state.addPendingGeneration);
  const removePendingGeneration = useGenerationStore((state) => state.removePendingGeneration);
  const clearPendingForImage = useGenerationStore((state) => state.clearPendingForImage);
  const invalidateCachedGenerationData = useGenerationStore((state) => state.invalidateCachedGenerationData);

  const removeShot = useShotlistStore((state) => state.removeShot);

  const vbAddPending = useVisionBoardStore((state) => state.addPendingGeneration);
  const vbRemovePending = useVisionBoardStore((state) => state.removePendingGeneration);
  const vbInvalidateCache = useVisionBoardStore((state) => state.invalidateCache);

  useEffect(() => {
    if (!isAuthenticated) return;

    const stopImageEditRefresh = subscribeToImageEdits(on, off, () => useProjectStore.getState().refreshImages());

    // Image events
    const onImageUploaded = ({ image }) => addImage(image);
    const onImageCategorized = ({ imageId, categoryId }) => updateImage(imageId, { category_id: categoryId });
    const onImagesBulkCategorized = ({ assignments }) => {
      for (const { imageId, categoryId } of assignments) {
        updateImage(imageId, { category_id: categoryId });
      }
    };
    const onImageMoved = ({ imageId, newCategoryId }) => moveImage(imageId, newCategoryId);
    const onImagesReordered = ({ imageIds }) => reorderImages(imageIds);

    // Category events
    const onCategoryCreated = ({ category }) => addCategory(category);
    const onCategoryRenamed = ({ categoryId, newName }) => updateCategory(categoryId, { name: newName });
    const onCategoryDeleted = ({ categoryId, imageIds }) => {
      removeCategory(categoryId);
      imageIds?.forEach(id => removeImage(id));
    };

    // Generation events — pending tracking (survives page navigation)
    // Also invalidate the generation-data cache so the next GenerationPage
    // mount fetches fresh data instead of serving stale cache.
    const onGenerationStarted = (data) => addPendingGeneration(data);
    const onGenerationComplete = ({ generation }) => {
      removePendingGeneration(generation.image_id, generation.id);
      invalidateCachedGenerationData(generation.image_id);
      updateImage(generation.image_id, {
        has_generation: 1,
      });
    };
    const onGenerationFailed = ({ imageId, generationId }) => {
      // Only clear pending for legacy events without generationId
      // (new events use DB-backed rows that transition to 'failed' status)
      if (imageId && !generationId) clearPendingForImage(imageId);
      if (imageId) invalidateCachedGenerationData(imageId);
      // A failed generation refunds the credits debited at request time — refresh the balance.
      useAuthStore.getState().refreshCredits();
    };
    // Cache invalidation for events that only had local GenerationPage handlers.
    // When these arrive while on a different tab, the cache entry is cleared
    // so the next visit re-fetches fresh data from the API.
    const onGradingUpdatedGlobal = ({ imageId, gradedPath, gradedThumbnailPath, displayPath }) => {
      invalidateCachedGenerationData(imageId);
      // Invalidate presigned URLs so other clients re-resolve fresh ones
      if (gradedPath) invalidateUrl(gradedPath);
      if (gradedThumbnailPath) invalidateUrl(gradedThumbnailPath);
      if (displayPath) invalidateUrl(displayPath);
      // Update the artboard thumbnail — always push the new path, even on
      // first-time grading (the old condition checked img.graded_thumbnail_path
      // which is null before the first grading, so it never updated).
      if (gradedThumbnailPath) {
        const img = useProjectStore.getState().images.find(i => i.id === imageId);
        if (img) {
          updateImage(imageId, { graded_thumbnail_path: gradedThumbnailPath });
        }
      }
    };
    const onCropUpdatedGlobal = ({ imageId, generationId, cropParams }) => {
      invalidateCachedGenerationData(imageId);
      const img = useProjectStore.getState().images.find(i => i.id === imageId);
      if (img && img.selected_generation_id === generationId) {
        updateImage(imageId, { crop_params: cropParams || null });
      }
    };
    const onGenerationDeletedGlobal = ({ imageId }) => invalidateCachedGenerationData(imageId);
    const onLikeUpdatedGlobal = ({ imageId, generationId }) => {
      // Server now includes imageId — use it directly instead of O(n) cache search
      if (imageId) {
        invalidateCachedGenerationData(imageId);
      } else {
        // Fallback for older broadcast format
        const cache = useGenerationStore.getState().generationDataCache;
        for (const [imgId, entry] of Object.entries(cache)) {
          if (entry.generations?.some(g => g.id === generationId)) {
            invalidateCachedGenerationData(imgId);
            break;
          }
        }
      }
    };

    const onDisplaySelectionChanged = ({ imageId, generationId, generatedThumbnailPath, gradedThumbnailPath, cropParams, displayUserLocked }) => {
      // Invalidate cached presigned URLs so components re-resolve fresh ones
      if (generatedThumbnailPath) invalidateUrl(generatedThumbnailPath);
      if (gradedThumbnailPath) invalidateUrl(gradedThumbnailPath);
      // Update projectStore — the single source of truth for display state.
      // ShotlistTable derives thumbnails from projectStore.images via imageMap,
      // so no separate shotlistStore update is needed.
      const update = {
        selected_generation_id: generationId,
        generated_thumbnail_path: generatedThumbnailPath,
        graded_thumbnail_path: gradedThumbnailPath || null,
        has_generation: generatedThumbnailPath ? 1 : 0,
        crop_params: cropParams || null,
      };
      if (displayUserLocked !== null && displayUserLocked !== undefined) {
        update.display_user_locked = displayUserLocked;
      }
      updateImage(imageId, update);
    };

    const onImageRotated = ({ image }) => {
      invalidateUrl(image.original_path);
      invalidateUrl(image.thumbnail_path);
      updateImage(image.id, {
        width: image.width,
        height: image.height,
        original_path: image.original_path,
        thumbnail_path: image.thumbnail_path,
      });
    };

    const onGenerationRotated = ({ imageId, generationId, newAspectRatio, newCropParams, newImageWidth, newImageHeight }) => {
      const img = useProjectStore.getState().images.find(i => i.id === imageId);
      if (img) {
        // Invalidate cached presigned URLs for rotated images
        if (img.thumbnail_path) invalidateUrl(img.thumbnail_path);
        if (img.generated_thumbnail_path) invalidateUrl(img.generated_thumbnail_path);
        if (img.graded_thumbnail_path) invalidateUrl(img.graded_thumbnail_path);
        updateImage(imageId, {
          width: newImageWidth,
          height: newImageHeight,
          crop_params: newCropParams || img.crop_params,
        });
      }
    };

    const onCommentUpdated = ({ imageId, comment }) => updateImage(imageId, { comment });
    const onFrameUpdated = ({ imageId, frameNumber }) => updateImage(imageId, { frame_number: frameNumber });
    const onDescriptionUpdated = ({ imageId, description }) => updateImage(imageId, { description });
    const onFilenamesUpdated = ({ updates }) => {
      updates?.forEach(({ imageId, filename }) => updateImage(imageId, { filename }));
    };
    const onShotHidden = ({ shotId }) => removeShot(shotId);

    // Mask regions broadcasts: keep cached image/generation state in sync.
    // The local useMaskRegions hook already merges incoming edits for the active
    // source — this handler ensures the project + generation caches stay fresh
    // for users on other tabs / pages.
    const onImageMaskRegionsUpdated = ({ imageId, regions }) => {
      updateImage(imageId, { mask_regions: regions || null });
    };
    const onGenerationMaskRegionsUpdated = ({ generationId }) => {
      // We don't have the generation ID → image map handy here; the local
      // hook on GenerationPage handles the live update. We only need to
      // invalidate the cache so a future load re-fetches the row.
      const cache = useGenerationStore.getState().generationDataCache;
      for (const [imgId, entry] of Object.entries(cache)) {
        if (entry.generations?.some(g => g.id === generationId)) {
          invalidateCachedGenerationData(imgId);
          break;
        }
      }
    };

    // Vision Board events
    const onVbStarted = (data) => vbAddPending(data);
    const onVbComplete = ({ generation }) => {
      vbRemovePending(generation.id);
      vbInvalidateCache(generation.project_id);
      useAuthStore.getState().refreshStorage();
    };
    const onVbFailed = ({ generationId, projectId: pid }) => {
      vbRemovePending(generationId);
      if (pid) vbInvalidateCache(pid);
    };
    const onVbDeleted = ({ generationId, projectId: pid }) => {
      if (pid) vbInvalidateCache(pid);
      useAuthStore.getState().refreshStorage();
    };

    // Member events
    const onMemberJoined = ({ user, role }) => {
      addMember({ user_id: user.id, username: user.username, display_name: user.display_name, role });
    };
    const onMemberRemoved = ({ userId }) => {
      removeMember(userId);
      dropPresence(userId);
      const currentUser = useAuthStore.getState().user;
      if (currentUser && userId === currentUser.id) {
        const realtimeProjectId = getCurrentProjectId();
        const pathMatch = appPathname().match(/^\/project\/([^/]+)/);
        const viewingProjectId = pathMatch?.[1];
        if (viewingProjectId && viewingProjectId === realtimeProjectId && !isExitingProject(viewingProjectId)) {
          replaceWithWebApp(webDashboardUrl(DashboardNotice.ACCESS_REMOVED));
        }
      }
    };
    const onMemberRoleUpdated = ({ userId, role }) => updateMemberRole(userId, role);
    const onInviteCreated = ({ invite }) => addPendingInvite(invite);
    const onInviteCancelled = ({ inviteId }) => removePendingInvite(inviteId);

    // Ownership transfer — update project owner in store and refresh stale data
    const onOwnershipTransferred = ({ oldOwnerId, newOwnerId }) => {
      const store = useProjectStore.getState();
      if (store.currentProject) {
        store.updateProject({ created_by: newOwnerId });
        // Force a member re-fetch so the owner/member rows reflect the swap, then reload (clearing
        // membersLoaded alone does not re-trigger a load while the team view is already open).
        useProjectStore.setState({ membersLoaded: false, members: [], pendingInvites: [] });
        store.loadMembers();
      }
      useAuthStore.getState().refreshStorage();
    };

    // Credits update — refresh project credits and personal credit balances
    const onCreditsUpdated = ({ pool, balance_after, projectId }) => {
      if (pool === 'project') {
        useProjectStore.getState().updateProjectCredits(projectId, balance_after);
      }
      // Always refresh personal credits — project refunds also affect purchased_credits
      useAuthStore.getState().refreshCredits();
    };

    const onProjectUpdated = ({ name, description }) => {
      useProjectStore.getState().updateProject({ name, description });
    };

    const onProjectDeleted = ({ projectId }) => {
      const pathMatch = appPathname().match(/^\/project\/([^/]+)/);
      const viewingProjectId = pathMatch?.[1];
      if (viewingProjectId && viewingProjectId === projectId && !isExitingProject(projectId)) {
        useProjectStore.getState().clearProject();
        replaceWithWebApp(webDashboardUrl(DashboardNotice.PROJECT_GONE));
      }
    };

    const onAccountDeleted = () => {
      useAuthStore.getState().logout();
    };

    // Register all handlers
    on('image-uploaded', onImageUploaded);
    on('image-categorized', onImageCategorized);
    on('images-bulk-categorized', onImagesBulkCategorized);
    on('image-moved', onImageMoved);
    on('images-reordered', onImagesReordered);
    on('category-created', onCategoryCreated);
    on('category-renamed', onCategoryRenamed);
    on('category-deleted', onCategoryDeleted);
    on('generation-started', onGenerationStarted);
    on('generation-complete', onGenerationComplete);
    on('generation-failed', onGenerationFailed);
    on('display-selection-changed', onDisplaySelectionChanged);
    on('image-rotated', onImageRotated);
    on('generation-rotated', onGenerationRotated);
    on('image-comment-updated', onCommentUpdated);
    on('image-frame-updated', onFrameUpdated);
    on('image-description-updated', onDescriptionUpdated);
    on('filenames-updated', onFilenamesUpdated);
    on('shotlist-shot-hidden', onShotHidden);
    on('member-joined', onMemberJoined);
    on('member-removed', onMemberRemoved);
    on('member-role-updated', onMemberRoleUpdated);
    on('invite-created', onInviteCreated);
    on('invite-cancelled', onInviteCancelled);
    on('ownership-transferred', onOwnershipTransferred);
    on('credits-updated', onCreditsUpdated);
    on('project-updated', onProjectUpdated);
    on('project-deleted', onProjectDeleted);
    on('account-deleted', onAccountDeleted);
    on('grading-updated', onGradingUpdatedGlobal);
    on('crop-updated', onCropUpdatedGlobal);
    on('generation-deleted', onGenerationDeletedGlobal);
    on('generation-like-updated', onLikeUpdatedGlobal);
    on('visionboard-generation-started', onVbStarted);
    on('visionboard-generation-complete', onVbComplete);
    on('visionboard-generation-failed', onVbFailed);
    on('visionboard-generation-deleted', onVbDeleted);
    on('image-mask-regions-updated', onImageMaskRegionsUpdated);
    on('generation-mask-regions-updated', onGenerationMaskRegionsUpdated);

    // Cleanup
    return () => {
      stopImageEditRefresh();
      off('image-uploaded', onImageUploaded);
      off('image-categorized', onImageCategorized);
      off('images-bulk-categorized', onImagesBulkCategorized);
      off('image-moved', onImageMoved);
      off('images-reordered', onImagesReordered);
      off('category-created', onCategoryCreated);
      off('category-renamed', onCategoryRenamed);
      off('category-deleted', onCategoryDeleted);
      off('generation-started', onGenerationStarted);
      off('generation-complete', onGenerationComplete);
      off('generation-failed', onGenerationFailed);
      off('display-selection-changed', onDisplaySelectionChanged);
      off('image-rotated', onImageRotated);
      off('generation-rotated', onGenerationRotated);
      off('image-comment-updated', onCommentUpdated);
      off('image-frame-updated', onFrameUpdated);
      off('image-description-updated', onDescriptionUpdated);
      off('filenames-updated', onFilenamesUpdated);
      off('shotlist-shot-hidden', onShotHidden);
      off('member-joined', onMemberJoined);
      off('member-removed', onMemberRemoved);
      off('member-role-updated', onMemberRoleUpdated);
      off('invite-created', onInviteCreated);
      off('invite-cancelled', onInviteCancelled);
      off('ownership-transferred', onOwnershipTransferred);
      off('credits-updated', onCreditsUpdated);
      off('project-updated', onProjectUpdated);
      off('project-deleted', onProjectDeleted);
      off('account-deleted', onAccountDeleted);
      off('grading-updated', onGradingUpdatedGlobal);
      off('crop-updated', onCropUpdatedGlobal);
      off('generation-deleted', onGenerationDeletedGlobal);
      off('generation-like-updated', onLikeUpdatedGlobal);
      off('visionboard-generation-started', onVbStarted);
      off('visionboard-generation-complete', onVbComplete);
      off('visionboard-generation-failed', onVbFailed);
      off('visionboard-generation-deleted', onVbDeleted);
      off('image-mask-regions-updated', onImageMaskRegionsUpdated);
      off('generation-mask-regions-updated', onGenerationMaskRegionsUpdated);
    };
  }, [
    isAuthenticated,
    addImage, updateImage, removeImage, moveImage, reorderImages,
    addCategory, updateCategory, removeCategory,
    addMember, updateMemberRole, removeMember, addPendingInvite, removePendingInvite,
    addPendingGeneration, removePendingGeneration, clearPendingForImage, invalidateCachedGenerationData,
    removeShot, vbAddPending, vbRemovePending, vbInvalidateCache,
  ]);

  return null;
}

function App() {
  // Subscribe to specific store slices for proper reactivity
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const accountId = useAuthStore((state) => state.user?.id);
  const authError = useAuthStore((state) => state.authError);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => watchSession(), []);

  return (
    <ErrorBoundary>
    <BrowserRouter key={accountId ?? 'signed-out'} basename={import.meta.env.BASE_URL.replace(/\/$/, '') || '/'}>
      <RealtimeEventListener />
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--color-surface-elevated)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
          },
          success: {
            iconTheme: {
              primary: 'rgb(var(--color-success))',
              secondary: 'var(--color-surface-elevated)',
            },
          },
          error: {
            iconTheme: {
              primary: 'var(--color-error)',
              secondary: 'var(--color-surface-elevated)',
            },
          },
        }}
      />

      <Suspense fallback={<FullPageLoading />}>
        {authError ? (
          <SessionCheckFailed onRetry={() => checkAuth()} />
        ) : (
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route element={<AuthLayout />}>
            <Route path="/invite/:token" element={<AcceptInvitePage />} />
          </Route>

          {/* All authenticated routes share AppLayout (persistent Sidebar) */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/account" element={<AccountSettings />} />
            <Route path="/account/storage" element={<StorageBreakdown />} />
            <Route path="/account/billing" element={<CreditsPage />} />
            <Route path="/project/:projectId" element={<ProjectLayout />}>
              <Route index element={<Navigate to="artboard" replace />} />
              <Route path="artboard" element={<ArtboardContainer />} />
              <Route path="artboard/:categoryId" element={<ArtboardContainer />} />
              <Route path="image/:imageId" element={<GenerationPage />} />
              <Route path="shotlist" element={<ShotlistPage />} />
              <Route path="storyboard" element={<StoryboardPage />} />
              <Route path="vision-board" element={<VisionBoardPage />} />
              <Route path="settings" element={<ProjectSettings />} />
              {/* The account pages again, inside the project shell, for links from within a project */}
              <Route path="account" element={<AccountSettings />} />
              <Route path="account/storage" element={<StorageBreakdown />} />
              <Route path="account/billing" element={<CreditsPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        )}
      </Suspense>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
